/**
 * Operations orders, missions 27-32: people (10 §2). Each states its special rule as an order, names the
 * equipment the job needs, and says how it ends. tests/unit/uiBriefing.test.ts holds a phrase from every rule
 * here and must be kept in step.
 */

import type { ArchetypeOrder } from './orders';
import {
  ALERTED, escortExtraction, fixed, FULL_ALARM, NO_NOISE_RULE, NOTHING_SPECIAL, ON_THE_CLOCK, QUIET_PISTOL, rules,
} from './orderKit';

type PeopleId =
  | 'downed-pilot' | 'defector' | 'eliminate-general' | 'snatch-scientist' | 'partisan-rendezvous' | 'prison-break';

export const PEOPLE_ORDERS: Readonly<Record<PeopleId, ArchetypeOrder>> = {
  'downed-pilot': {
    situation: 'ONE OF OUR PILOTS IS DOWN AND HIDING IN THE RUINS. HE IS WOUNDED. ENEMY SEARCH PARTIES ARE SWEEPING'
      + ' TOWARD HIM.',
    mission: fixed('FIND THE PILOT AND BRING HIM OUT.'),
    execution: [
      'FIND HIM IN THE RUINS AND INTERACT TO GET HIM ON HIS FEET. HE FOLLOWS YOU.',
      'HE IS WOUNDED: HE WALKS, HE NEVER RUNS. PLAN EVERY LEG AT HIS PACE.',
    ],
    special: rules(
      `THE SEARCH PARTIES REACH THE RUINS AT A STATED TIME. ${ON_THE_CLOCK} IF THEY FIND HIM BEFORE YOU DO, THE`
        + ' MISSION HAS FAILED.',
      NO_NOISE_RULE,
    ),
    equipment: [`${NOTHING_SPECIAL} TRAVEL LIGHT ON THE WAY IN: YOU ARE RACING THE SEARCH PARTIES.`],
    extraction: [
      'RECALL EARLY. THE OSPREY NEEDS 45 SECONDS; THE PILOT, AT A WALK, NEEDS LONGER THAN THAT TO REACH THE X.',
      'THE PILOT BOARDS BEFORE YOU. DEFEND THE LADDER UNTIL HE IS UP.',
    ],
  },
  defector: {
    situation: 'AN ENEMY OFFICER WANTS TO COME OVER. HE WILL BE AT THE RENDEZVOUS AT A STATED TIME, AND NOT BEFORE.',
    mission: fixed('MEET HIM AT THE RENDEZVOUS AND ESCORT HIM OUT.'),
    execution: [
      `HE COMES AT A STATED TIME. ${ON_THE_CLOCK} BE AT THE RENDEZVOUS WHEN HE ARRIVES AND INTERACT TO MEET HIM. HE`
        + ' FOLLOWS YOU.',
      'HE IS UNARMED AND NO SOLDIER. UNDER FIRE HE GOES TO GROUND, AND HE WILL NOT CROSS OPEN GROUND UNTIL THE'
        + ' SHOOTING STOPS.',
    ],
    special: rules(
      `${ALERTED} BEFORE THE MEETING, HE DOES NOT COME AND THE MISSION HAS FAILED. STAY QUIET UNTIL YOU HAVE HIM.`,
      'KEEP HIM ALIVE. HE IS THE MISSION.',
    ),
    equipment: [NOTHING_SPECIAL, QUIET_PISTOL],
    extraction: escortExtraction(
      'THE DEFECTOR', 'CLEAR THE PICKUP BEFORE YOU CALL: HE WILL NOT WALK TO THE LADDER UNDER FIRE.',
    ),
  },
  'eliminate-general': {
    situation: 'A GENERAL IS INSPECTING THE COMPOUND. BODYGUARDS WALK WITH HIM. HIS STAFF CAR IS WAITING.',
    mission: fixed('KILL THE GENERAL.'),
    execution: [
      'HE MOVES ABOUT THE COMPOUND WITH HIS BODYGUARDS. FIND HIM ON THE PASS OVER THE TARGET IF YOU CAN.',
      'ONE CLEAN SHOT IS THE WHOLE MISSION. CHOOSE WHERE YOU TAKE IT FROM, AND HOW YOU LEAVE.',
    ],
    special: rules(
      `KNIFE ONLY UNTIL THE SHOT. ${ALERTED} HE IS HURRIED TO HIS CAR AND IS GONE 30 SECONDS LATER. IF HE LEAVES`
        + ' ALIVE THE MISSION HAS FAILED.',
      'YOUR FIRST LOUD ROUND SHOULD BE THE ONE THAT KILLS HIM. IF IT IS NOT, YOU HAVE THOSE 30 SECONDS.',
    ),
    equipment: [NOTHING_SPECIAL, QUIET_PISTOL],
  },
  'snatch-scientist': {
    situation: 'A LABORATORY UNDER GUARD. THE SCIENTIST WORKS INSIDE. HE IS NO SOLDIER AND HE NEVER FIGHTS.',
    mission: fixed('TAKE THE SCIENTIST ALIVE AND BRING HIM OUT.'),
    execution: [
      'CLOSE ON HIM WITH A WEAPON UP AND HE SURRENDERS. HE WALKS WITH YOU.',
      'HE MUST LIVE. DEAD, THE MISSION HAS FAILED. NO EXPLOSIVES NEAR THE LABORATORY.',
    ],
    special: rules(
      `AT ${FULL_ALARM} THE GUARDS ARE ORDERED TO SHOOT HIM RATHER THAN LOSE HIM. IF HE IS STILL AT LARGE THEY DO`
        + ' IT WHERE HE STANDS; IF HE IS WITH YOU THEY COME FOR HIM.',
      'THERE IS NO KNIFE RULE, BUT KEEP IT BELOW A FULL ALARM UNTIL HE IS YOURS. THEN MOVE.',
    ),
    equipment: [NOTHING_SPECIAL],
    extraction: escortExtraction('THE SCIENTIST', 'DEFEND THE LADDER UNTIL HE IS UP.'),
  },
  'partisan-rendezvous': {
    situation: 'A PARTISAN BAND IN THE HILLS HAS LOST ITS RADIO. THEIR CAMP LIES NORTH OF THE DROP, BEYOND THE ENEMY'
      + ' OUTPOSTS.',
    mission: fixed('CARRY THE RADIO SET TO THE PARTISAN CAMP AND HAND IT OVER.'),
    execution: [
      'THE RADIO SET RIDES IN POD A. OPEN POD A AND IT IS ON YOUR BACK.',
      'AT THE CAMP, INTERACT TO HAND IT OVER.',
    ],
    special: rules(
      'POD A MUST BE DROPPED, AND IT MUST LAND INTACT. LEAVE IT ABOARD - OR PUT IT IN WATER, A TRENCH, A MINEFIELD'
        + ' OR ON A ROOF - AND THE MISSION IS LOST AT THE DROP. THE FLYOVER MATTERS: PICK OPEN GROUND FOR POD A.',
      NO_NOISE_RULE,
    ),
    equipment: [
      'THE RADIO SET WEIGHS 8 ON YOUR BACK UNTIL IT IS DELIVERED: YOU RUN SLOWER AND TIRE FASTER. PACK LIGHT, AND'
        + ' PUT WHAT ELSE YOU NEED IN PODS B AND C.',
    ],
  },
  'prison-break': {
    situation: 'YOU WERE TAKEN. YOU ARE IN A CELL BLOCK INSIDE THE WIRE. THE CELL DOOR IS AJAR AND NOBODY HAS'
      + ' LOOKED IN YET.',
    mission: fixed('GET OUT OF THE CAMP, REACH YOUR SUPPLY PODS OUTSIDE THE WIRE, AND EXTRACT.'),
    execution: [
      'NO INSERTION: THERE IS NO FLYOVER AND NO JUMP. YOU START IN THE CELL WITH YOUR KNIFE AND NOTHING ELSE.',
      'THE WIRE BEHIND THE CELL BLOCK HAS ALREADY BEEN CUT. YOU DO NOT NEED CUTTERS: FIND THE GAP AND GO THROUGH IT.',
      'YOUR PODS FELL OUTSIDE THE WIRE AND ARE MARKED. REACHING THEM IS THE OBJECTIVE; WHAT IS IN THEM GETS YOU HOME.',
      'THE KNIFE IS SILENT FROM BEHIND. A BODY LEFT IN THE OPEN IS NOT.',
    ],
    special: rules(
      `THE ALARM IS RAISED WHEN THE CELL IS FOUND EMPTY, AT A STATED TIME. ${ON_THE_CLOCK} WHEN IT REACHES ZERO THE`
        + ' WHOLE GARRISON GOES TO A FULL ALARM AND COMES LOOKING, STARTING AT THE CELL. BE THROUGH THE WIRE BY THEN.',
    ),
    equipment: [
      'NOTHING YOU PACK ON THE RANGER GOES INTO THE CELL WITH YOU: IT WAS TAKEN OFF YOU AND WAITS IN POD A, ON TOP OF'
        + ' WHATEVER YOU PACKED THERE. RARE GEAR IS LOST. REACH POD A AND YOU ARE ARMED AGAIN.',
    ],
  },
};
